import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { DatePicker, Spin, TimeRangePickerProps } from 'antd'
import { LucideIcon, type LucideIconName } from 'components/LucideIcon'
import { SearchBar } from 'components/SearchBar'
import { formatDistanceToNow } from 'date-fns'
import dayjs from 'dayjs'
import { activeDateFnsLocale } from 'features/locale/dateFnsLocale'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import {
  NOTIFICATION_STATUS_MESSAGES,
  parseTimestamp,
  stripHtml,
} from './notificationUtils'
import { type Notification, NotificationStatus } from './useNotifications'

export interface DateRange {
  start: string
  end: string
}

export type SortDirection = 'ascending' | 'descending'

export const DEFAULT_SORT_DIRECTION: SortDirection = 'descending'

export const SORT_DIRECTION_CONFIG: Record<
  SortDirection,
  {
    label: MessageDescriptor
    iconName: LucideIconName
    ariaLabel: MessageDescriptor
    next: SortDirection
  }
> = {
  descending: {
    label: msg`Newest first`,
    iconName: 'ArrowDownNarrowWide',
    ariaLabel: msg`Sort oldest first`,
    next: 'ascending',
  },
  ascending: {
    label: msg`Oldest first`,
    iconName: 'ArrowUpNarrowWide',
    ariaLabel: msg`Sort newest first`,
    next: 'descending',
  },
}

export const compareByTimestamp = (
  first: Notification,
  second: Notification,
  direction: SortDirection
): number => {
  const firstTimestamp = first.delivered_at ?? first.created_at
  const secondTimestamp = second.delivered_at ?? second.created_at
  const firstTime = firstTimestamp
    ? parseTimestamp(firstTimestamp).getTime()
    : 0
  const secondTime = secondTimestamp
    ? parseTimestamp(secondTimestamp).getTime()
    : 0
  const difference =
    (Number.isNaN(secondTime) ? 0 : secondTime) -
    (Number.isNaN(firstTime) ? 0 : firstTime)
  return direction === 'descending' ? difference : -difference
}

const buildDateRangePresets = (
  translate: (descriptor: MessageDescriptor) => string
): TimeRangePickerProps['presets'] => [
  {
    label: translate(msg`Last 7 Days`),
    value: [dayjs().add(-7, 'd'), dayjs()],
  },
  {
    label: translate(msg`Last 14 Days`),
    value: [dayjs().add(-14, 'd'), dayjs()],
  },
  {
    label: translate(msg`Last 30 Days`),
    value: [dayjs().add(-30, 'd'), dayjs()],
  },
  {
    label: translate(msg`Last 90 Days`),
    value: [dayjs().add(-90, 'd'), dayjs()],
  },
]

type StatusTab = NotificationStatus

const STATUS_TABS: StatusTab[] = [
  NotificationStatus.UNREAD,
  NotificationStatus.READ,
  NotificationStatus.ARCHIVED,
]

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.forecast.background2};
`

const Header = styled.div`
  padding: 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const HeaderTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  margin: 0;
`

const MarkAllReadButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
    background-color: ${({ theme }) => theme.forecast.background3};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const TabBar = styled.div`
  display: flex;
  gap: 0.25rem;
  background-color: ${({ theme }) => theme.forecast.background3};
  border-radius: 0.375rem;
  padding: 0.125rem;
`

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.3125rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  background-color: ${({ theme, $active }) =>
    $active ? theme.forecast.background2 : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.forecast.activeText : theme.forecast.secondaryText};
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'};

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const DateFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .ant-picker {
    flex: 1;
    min-width: 0;
    background-color: ${({ theme }) => theme.forecast.defaultButton};
    border-color: ${({ theme }) => theme.forecast.border};
    border-radius: 4px;

    &:hover {
      background-color: ${({ theme }) => theme.forecast.hoverButton};
    }

    input {
      font-size: 0.75rem;
      color: ${({ theme }) => theme.forecast.defaultText};
    }
  }
`

const DateLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: nowrap;
`

const SortRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const SortLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: nowrap;
`

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  background: ${({ theme }) => theme.forecast.defaultButton};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.25rem;
`

const ItemContainer = styled.div<{ $isUnread: boolean; $isSelected: boolean }>`
  padding: 0.75rem;
  border-radius: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  border-left: 3px solid
    ${({ theme, $isUnread }) =>
      $isUnread
        ? (theme.forecast.primary ?? theme.forecast.defaultButton)
        : 'transparent'};
  background-color: ${({ theme, $isSelected, $isUnread }) =>
    $isSelected
      ? theme.forecast.background3
      : $isUnread
        ? theme.forecast.background2_5
        : theme.forecast.background2};
  opacity: ${({ $isUnread, $isSelected }) =>
    $isUnread || $isSelected ? 1 : 0.7};
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
  }
`

const ReadToggleButton = styled.button<{ $isUnread: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.125rem;
  cursor: pointer;
  color: ${({ theme, $isUnread }) =>
    $isUnread
      ? (theme.forecast.primary ?? theme.forecast.defaultButton)
      : theme.forecast.secondaryText + '88'};
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) =>
      theme.forecast.primary ?? theme.forecast.defaultButton};
    transform: scale(1.15);
  }
`

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`

const ItemTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  margin-bottom: 0.125rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ItemBody = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.375rem;
`

const ItemTimestamp = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ItemSubtitle = styled.div`
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 50%;
`

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  padding: 2rem;
  text-align: center;
`

const LoadingState = styled(EmptyState)``

const ErrorState = styled(EmptyState)`
  color: ${({ theme }) => theme.forecast.danger ?? theme.common.alert.error};
`

const ScrollSentinel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  min-height: 2rem;
`

interface NotificationListPanelProps {
  notifications: Notification[]
  total: number
  isLoading: boolean
  isLoadingMore: boolean
  error: Error | undefined
  selectedId: string | undefined
  searchQuery: string
  statusTab: StatusTab
  dateRange: DateRange | undefined
  hasUnread: boolean
  sortDirection: SortDirection
  onSelectNotification: (id: string) => void
  onToggleRead: (id: string, newStatus: NotificationStatus) => void
  onSearchChange: (query: string) => void
  onStatusTabChange: (tab: StatusTab) => void
  onDateRangeChange: (range: DateRange | undefined) => void
  onSortDirectionChange: (direction: SortDirection) => void
  onMarkAllRead: () => void
  onLoadMore: () => void
  canLoadMore: boolean
}

export type { StatusTab }

export const NotificationListPanel: React.FC<NotificationListPanelProps> = ({
  notifications,
  total,
  isLoading,
  isLoadingMore,
  error,
  selectedId,
  searchQuery,
  statusTab,
  dateRange,
  hasUnread,
  sortDirection,
  onSelectNotification,
  onToggleRead,
  onSearchChange,
  onStatusTabChange,
  onDateRangeChange,
  onSortDirectionChange,
  onMarkAllRead,
  onLoadMore,
  canLoadMore,
}) => {
  const { t, i18n } = useLingui()
  const dateRangePresets = useMemo(() => buildDateRangePresets(t), [t])
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && canLoadMore && !isLoadingMore) {
          onLoadMore()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [canLoadMore, isLoadingMore, onLoadMore])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value)
    },
    [onSearchChange]
  )

  const handleDateRangeChange = useCallback(
    (values: ReadonlyArray<dayjs.Dayjs> | null) => {
      if (!values || values.length !== 2 || !values[0] || !values[1]) {
        onDateRangeChange(undefined)
        return
      }
      onDateRangeChange({
        start: values[0].startOf('day').toISOString(),
        end: values[1].endOf('day').toISOString(),
      })
    },
    [onDateRangeChange]
  )

  return (
    <Container>
      <Header>
        <HeaderTopRow>
          <HeaderTitle>
            <Trans>Notifications ({total})</Trans>
          </HeaderTitle>
          {hasUnread && (
            <MarkAllReadButton onClick={onMarkAllRead} type='button'>
              <Trans>Mark all as read</Trans>
            </MarkAllReadButton>
          )}
        </HeaderTopRow>

        <TabBar>
          {STATUS_TABS.map((key) => (
            <Tab
              key={key}
              $active={statusTab === key}
              onClick={() => onStatusTabChange(key)}
              type='button'>
              {i18n._(NOTIFICATION_STATUS_MESSAGES[key])}
            </Tab>
          ))}
        </TabBar>

        <SearchBar
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={t`Filter notifications...`}
        />

        <DateFilterRow>
          <DateLabel>
            <Trans>Date</Trans>
          </DateLabel>
          <DatePicker.RangePicker
            size='small'
            value={
              dateRange
                ? [dayjs(dateRange.start), dayjs(dateRange.end)]
                : undefined
            }
            onChange={(vals) =>
              handleDateRangeChange(
                (vals as ReadonlyArray<dayjs.Dayjs>) || null
              )
            }
            presets={dateRangePresets}
            allowEmpty={[true, true]}
            allowClear
          />
        </DateFilterRow>

        <SortRow>
          <SortLabel>
            <Trans>Sort</Trans>
          </SortLabel>
          <SortButton
            onClick={() =>
              onSortDirectionChange(SORT_DIRECTION_CONFIG[sortDirection].next)
            }
            type='button'
            aria-label={i18n._(SORT_DIRECTION_CONFIG[sortDirection].ariaLabel)}>
            <LucideIcon
              name={SORT_DIRECTION_CONFIG[sortDirection].iconName}
              size={14}
            />
            {i18n._(SORT_DIRECTION_CONFIG[sortDirection].label)}
          </SortButton>
        </SortRow>
      </Header>

      {isLoading ? (
        <LoadingState>
          <Trans>Loading notifications...</Trans>
        </LoadingState>
      ) : error ? (
        <ErrorState>
          <Trans>Failed to load notifications</Trans>
        </ErrorState>
      ) : notifications.length === 0 ? (
        <EmptyState>
          {searchQuery
            ? t`No notifications match your search`
            : statusTab === NotificationStatus.ARCHIVED
              ? t`No archived notifications`
              : statusTab === NotificationStatus.UNREAD
                ? t`No unread notifications`
                : t`No notifications`}
        </EmptyState>
      ) : (
        <List>
          {notifications.map((notification) => (
            <NotificationListItem
              key={notification.id}
              notification={notification}
              isSelected={notification.id === selectedId}
              onSelect={onSelectNotification}
              onToggleRead={onToggleRead}
            />
          ))}
          <ScrollSentinel ref={sentinelRef}>
            {isLoadingMore && <Spin size='small' />}
          </ScrollSentinel>
        </List>
      )}
    </Container>
  )
}

interface NotificationListItemProps {
  notification: Notification
  isSelected: boolean
  onSelect: (id: string) => void
  onToggleRead: (id: string, newStatus: NotificationStatus) => void
}

const NotificationListItemInner: React.FC<NotificationListItemProps> = ({
  notification,
  isSelected,
  onSelect,
  onToggleRead,
}) => {
  const { t } = useLingui()
  const timestamp = notification.delivered_at ?? notification.created_at

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onToggleRead(
        notification.id,
        notification.isUnread
          ? NotificationStatus.READ
          : NotificationStatus.UNREAD
      )
    },
    [notification.id, notification.isUnread, onToggleRead]
  )

  return (
    <ItemContainer
      $isUnread={notification.isUnread}
      $isSelected={isSelected}
      onClick={() => onSelect(notification.id)}>
      <ItemContent>
        {notification.title && <ItemTitle>{notification.title}</ItemTitle>}
        <ItemBody>
          {notification.body
            ? stripHtml(notification.body)
            : notification.message}
        </ItemBody>
        <ItemFooter>
          {timestamp && (
            <ItemTimestamp>
              {formatDistanceToNow(parseTimestamp(timestamp), {
                addSuffix: true,
                locale: activeDateFnsLocale(),
              })}
            </ItemTimestamp>
          )}
          {notification.subtitle && (
            <ItemSubtitle>{notification.subtitle}</ItemSubtitle>
          )}
        </ItemFooter>
      </ItemContent>
      <ReadToggleButton
        $isUnread={notification.isUnread}
        onClick={handleToggle}
        type='button'
        aria-label={notification.isUnread ? t`Mark as read` : t`Mark as unread`}
        title={notification.isUnread ? t`Mark as read` : t`Mark as unread`}>
        <LucideIcon
          name={notification.isUnread ? 'Mail' : 'MailOpen'}
          size={14}
        />
      </ReadToggleButton>
    </ItemContainer>
  )
}

const NotificationListItem = React.memo(NotificationListItemInner)
NotificationListItem.displayName = 'NotificationListItem'
